import type { ArrowKind, ICanvasArrow } from "@/types/canvasTypes";

/**
 * Canvas assessment utility (Item 7, Stage 6.3).
 *
 * Compares a student's canvas to an expert's, producing per-node and
 * per-edge outcomes. Node correspondence is determined by
 * `source_annotation_client_id` — the link to the reader highlight a
 * rectangle was dragged from. Rectangles without provenance (created
 * directly on the canvas or from before Stage 6.0) are excluded from
 * node correspondence and counted as orphans.
 *
 * Multi-instance: a single annotation may be dragged onto the canvas
 * multiple times. Each expert node consumes the first encountered
 * student rect with a matching source ID (greedy one-to-one); any
 * additional student rects with the same source are "extras" —
 * informational, not penalized. Edge coverage allows any student arrow
 * between matching-source rect pairs to satisfy an expert edge.
 *
 * Pure graph structure here; Parsons-condition parity is layered on
 * by selectCanvasGrade by calling `evaluateArgument` separately on
 * each canvas.
 */

// We accept any structural rectangle/arrow shape — both the strict
// canvasTypes.ts Rectangle and the looser workspace.ts Rectangle
// satisfy these. Keeps the util free of cross-type imports.
export interface CheckableArrow {
  endRectIdx: number;
  kind: ArrowKind;
  // Additional fields (tag, credence, doNotRender) accepted but unused.
}
export interface CheckableRectangle {
  text?: string;
  category?: string;
  arrows: CheckableArrow[] | ICanvasArrow[];
  source_annotation_client_id?: string;
}

export interface NodeOutcome<R extends CheckableRectangle = CheckableRectangle> {
  expert: R;
  expertIdx: number;
  bestStudent: R | null;
  bestStudentIdx: number | null;
  /** Undefined when bestStudent is null. */
  categoriesAgree?: boolean;
  /**
   * Stage 6.9: reader-side credit for the matched pair, in [0, 1].
   * Defaults to 1 when no bridge is provided (legacy identity-match
   * behavior). When 0 < credit < 1 the underlying annotation was a
   * partial match against the expert highlight.
   */
  credit: number;
}

/**
 * Stage 6.9: maps a student annotation's client_id to the expert
 * highlight it best-matched at the reader level, along with the
 * fractional credit and whether the highlight categories agreed.
 *
 * When provided, assessCanvas matches student rect ↔ expert rect by
 * (student.src → bridge → expert.src) instead of expecting the same
 * source ID on both sides — Stage 3.6 made annotations per-user so
 * student/expert source IDs live in different UUID spaces. The bridge
 * is the only way to bridge them.
 *
 * When the bridge is undefined or has no entry for a given source ID,
 * matching falls back to source-ID identity (owner viewing their own
 * canvas, where both ends point at the same annotation).
 */
export interface SourceBridge {
  bySource: Map<
    string,
    { expertSourceId: string; credit: number; categoriesAgreeAtReader: boolean }
  >;
}

export interface EdgeOutcome {
  /** Source IDs of the expert edge endpoints. */
  fromSourceId: string;
  toSourceId: string;
  kind: ArrowKind;
  /**
   * True iff the student canvas has any arrow between any pair of
   * student rects whose source IDs match (fromSourceId, toSourceId)
   * with the same effective direction.
   */
  matched: boolean;
}

export interface CanvasAssessmentResult<R extends CheckableRectangle = CheckableRectangle> {
  // Per-expert-node outcomes — one entry per expert rectangle with
  // provenance. Orphan experts (no source ID) are excluded.
  nodeOutcomes: NodeOutcome<R>[];

  /** Student rectangles with no greedy pair (and a source ID). */
  extraStudents: R[];

  // Node-level counts (derived from nodeOutcomes for convenience).
  matchedNodeCount: number;
  missingNodeCount: number;
  miscategorizedCount: number;
  /**
   * Stage 6.9: sum of per-pair credits across matched outcomes.
   * Equals matchedNodeCount when no bridge is provided (all pairs
   * count as credit 1); below it when partial matches are present.
   * Used by selectCanvasGrade for graduated coverage scoring.
   */
  totalCoverageCredit: number;

  // Edge-level outcomes — one entry per expert edge whose endpoints
  // both have provenance. Edges touching an orphan expert rect are
  // excluded (we can't ask "did the student match this edge" without
  // a source ID for each endpoint).
  edgeOutcomes: EdgeOutcome[];
  matchedEdgeCount: number;
  missingEdgeCount: number;
  /**
   * Student arrows between paired-source rect endpoints that have no
   * counterpart in the expert canvas. Informational only.
   */
  extraEdgeCount: number;

  // Informational counts.
  /** Student rects with the same source ID as another already-claimed student. */
  duplicateStudentCount: number;
  /** Student rects with no source_annotation_client_id. */
  orphanStudentCount: number;
  /** Expert rects with no source_annotation_client_id. */
  orphanExpertCount: number;
}

/**
 * Normalize an edge to a directed shape for matching purposes.
 * "directed" preserves order. "bidirectional" and "undirected" are
 * treated as orderless — represented by sorting the two endpoint
 * source IDs lexicographically so two students can match an expert's
 * bidirectional regardless of which end they drew first.
 */
function edgeMatchKey(fromSrc: string, toSrc: string, kind: ArrowKind): string {
  if (kind === "directed") return `D:${fromSrc}>${toSrc}`;
  const [a, b] = fromSrc <= toSrc ? [fromSrc, toSrc] : [toSrc, fromSrc];
  return `${kind === "bidirectional" ? "B" : "U"}:${a}-${b}`;
}

export function assessCanvas<R extends CheckableRectangle>(
  studentRects: R[],
  expertRects: R[],
  bridge?: SourceBridge
): CanvasAssessmentResult<R> {
  // Normalize text for fuzzy matching: trim + collapse whitespace +
  // lowercase. Two annotations of the same passage typically produce
  // identical text after this; meaningful for the text-fallback below.
  const normalizeText = (s: string): string =>
    s.trim().replace(/\s+/g, " ").toLowerCase();

  // Pre-build expert lookups so the per-student resolution below is
  // cheap. expertSrcIds for the identity check (owner views own);
  // expertSrcByText for the divergence fallback when an expert canvas
  // rect was saved with a srcId that no longer appears in the reading
  // rubric (e.g., owner deleted/re-created the underlying highlight
  // between saving canvas rubric and reading rubric).
  const expertSrcIds = new Set<string>();
  const expertSrcByText = new Map<string, string>();
  for (const r of expertRects) {
    const src = r.source_annotation_client_id;
    if (!src) continue;
    expertSrcIds.add(src);
    if (r.text) {
      const key = normalizeText(r.text);
      // First rect wins for a given text — duplicate text on expert
      // side is informational only.
      if (!expertSrcByText.has(key)) expertSrcByText.set(key, src);
    }
  }

  // Resolve a student rect to (expertSourceId, credit) for matching.
  // Stage 6.9: the bridge gives us reader-side credit (annotation
  // overlap) but its expertSourceId may not be on the expert canvas —
  // an expert highlight ID can diverge from the canvas rect's saved
  // srcId when the owner edited highlights between saving the two
  // rubrics. Preserve the bridge's credit, but resolve the expert
  // canvas srcId through whichever path actually hits the snapshot:
  // direct bridge hit, identity, or text.
  const lookupExpertSource = (
    rect: R
  ): { expertSourceId: string; credit: number } => {
    const studentSrc = rect.source_annotation_client_id ?? "";
    const bridgeEntry = studentSrc ? bridge?.bySource.get(studentSrc) : undefined;
    const credit = bridgeEntry?.credit ?? 1;

    if (bridgeEntry && expertSrcIds.has(bridgeEntry.expertSourceId)) {
      return { expertSourceId: bridgeEntry.expertSourceId, credit };
    }
    if (studentSrc && expertSrcIds.has(studentSrc)) {
      return { expertSourceId: studentSrc, credit };
    }
    if (rect.text) {
      const aliased = expertSrcByText.get(normalizeText(rect.text));
      if (aliased) return { expertSourceId: aliased, credit };
    }
    // Give up: return whatever we have, knowing it likely won't match
    // any expert canvas rect (becomes an extra/missing).
    return {
      expertSourceId: bridgeEntry?.expertSourceId ?? studentSrc,
      credit,
    };
  };

  // ---- Node matching: greedy one-to-one by bridge-resolved expert source.
  const claimedStudentIdx = new Set<number>();

  const nodeOutcomes: NodeOutcome<R>[] = [];
  let orphanExpertCount = 0;
  let miscategorizedCount = 0;
  let matchedNodeCount = 0;
  let missingNodeCount = 0;
  let totalCoverageCredit = 0;

  // Build studentRect lookup by their RESOLVED expert source ID. Each
  // student rect's reader-matched expert ID becomes the key. Multi-
  // instance: keep ordered indices so first unclaimed wins.
  const studentIdxByResolvedExpertSrc = new Map<string, number[]>();
  const studentCreditByIdx = new Map<number, number>();
  studentRects.forEach((r, i) => {
    if (!r.source_annotation_client_id) return;
    const { expertSourceId, credit } = lookupExpertSource(r);
    studentCreditByIdx.set(i, credit);
    const arr = studentIdxByResolvedExpertSrc.get(expertSourceId) ?? [];
    arr.push(i);
    studentIdxByResolvedExpertSrc.set(expertSourceId, arr);
  });

  expertRects.forEach((expert, expertIdx) => {
    const srcId = expert.source_annotation_client_id;
    if (!srcId) {
      // Orphan expert (no provenance): student can't match it via the
      // source-ID path. Record as a missing outcome so it still ghosts
      // — saved expert canvases from before Stage 6.0 are mostly in
      // this category.
      orphanExpertCount++;
      missingNodeCount++;
      nodeOutcomes.push({
        expert,
        expertIdx,
        bestStudent: null,
        bestStudentIdx: null,
        credit: 0,
      });
      return;
    }
    const candidates = studentIdxByResolvedExpertSrc.get(srcId) ?? [];
    let pairedIdx: number | null = null;
    for (const idx of candidates) {
      if (!claimedStudentIdx.has(idx)) {
        pairedIdx = idx;
        claimedStudentIdx.add(idx);
        break;
      }
    }

    if (pairedIdx === null) {
      missingNodeCount++;
      nodeOutcomes.push({
        expert,
        expertIdx,
        bestStudent: null,
        bestStudentIdx: null,
        credit: 0,
      });
      return;
    }

    matchedNodeCount++;
    const student = studentRects[pairedIdx]!;
    const credit = studentCreditByIdx.get(pairedIdx) ?? 1;
    totalCoverageCredit += credit;
    // Category check is only meaningful when the underlying highlights
    // overlapped fully — below threshold the canvas-side category
    // signal is dominated by reader-credit noise.
    const categoriesAgree =
      credit === 1
        ? (student.category ?? "") === (expert.category ?? "")
        : undefined;
    if (categoriesAgree === false) miscategorizedCount++;
    nodeOutcomes.push({
      expert,
      expertIdx,
      bestStudent: student,
      bestStudentIdx: pairedIdx,
      categoriesAgree,
      credit,
    });
  });

  // ---- Extras and orphans.
  const extraStudents: R[] = [];
  let orphanStudentCount = 0;
  let duplicateStudentCount = 0;
  studentRects.forEach((r, i) => {
    if (!r.source_annotation_client_id) {
      orphanStudentCount++;
      return;
    }
    if (claimedStudentIdx.has(i)) return;
    const { expertSourceId } = lookupExpertSource(r);
    const candidatesForExpert =
      studentIdxByResolvedExpertSrc.get(expertSourceId) ?? [];
    const anyClaimed = candidatesForExpert.some((idx) => claimedStudentIdx.has(idx));
    if (anyClaimed) {
      duplicateStudentCount++;
    } else {
      extraStudents.push(r);
    }
  });

  // ---- Edge matching by source-ID pair + kind. Keys use the
  // bridge-resolved expert source IDs so student and expert edges live
  // in the same key space.
  const studentEdgeKeys = new Set<string>();
  studentRects.forEach((from) => {
    if (!from.source_annotation_client_id) return;
    const fromExpertSrc = lookupExpertSource(from).expertSourceId;
    for (const arrow of from.arrows) {
      const to = studentRects[arrow.endRectIdx];
      if (!to) continue;
      if (!to.source_annotation_client_id) continue;
      const toExpertSrc = lookupExpertSource(to).expertSourceId;
      studentEdgeKeys.add(edgeMatchKey(fromExpertSrc, toExpertSrc, arrow.kind));
    }
  });

  // For each expert edge between provenance-bearing endpoints, check
  // whether the student has an equivalent edge.
  const edgeOutcomes: EdgeOutcome[] = [];
  const expertEdgeKeys = new Set<string>();
  expertRects.forEach((from, _fromIdx) => {
    const fromSrc = from.source_annotation_client_id;
    if (!fromSrc) return;
    for (const arrow of from.arrows) {
      const to = expertRects[arrow.endRectIdx];
      if (!to) continue;
      const toSrc = to.source_annotation_client_id;
      if (!toSrc) continue;
      const key = edgeMatchKey(fromSrc, toSrc, arrow.kind);
      // De-dup multi-instance expert edges
      if (expertEdgeKeys.has(key)) continue;
      expertEdgeKeys.add(key);
      edgeOutcomes.push({
        fromSourceId: fromSrc,
        toSourceId: toSrc,
        kind: arrow.kind,
        matched: studentEdgeKeys.has(key),
      });
    }
  });
  const matchedEdgeCount = edgeOutcomes.filter((e) => e.matched).length;
  const missingEdgeCount = edgeOutcomes.length - matchedEdgeCount;

  // Extras: student edges between provenance-bearing endpoints that
  // don't appear in the expert key set.
  let extraEdgeCount = 0;
  studentEdgeKeys.forEach((key) => {
    if (!expertEdgeKeys.has(key)) extraEdgeCount++;
  });

  return {
    nodeOutcomes,
    extraStudents,
    matchedNodeCount,
    missingNodeCount,
    miscategorizedCount,
    totalCoverageCredit,
    edgeOutcomes,
    matchedEdgeCount,
    missingEdgeCount,
    extraEdgeCount,
    duplicateStudentCount,
    orphanStudentCount,
    orphanExpertCount,
  };
}
